import { WidgetContainer } from '@/components/analytics/WidgetContainer';
import type { ArticleAnalyticsData } from '@/types/analytics';
import styles from './ArticleStats.module.scss';

export interface ArticleStatsProps {
  data: ArticleAnalyticsData | null;
  isLoading?: boolean;
  error?: Error | null;
}

/**
 * Article Stats Widget
 *
 * Displays total articles published, public articles, and total views.
 */
export function ArticleStats({ data, isLoading, error }: ArticleStatsProps) {
  const isEmpty = !data || data.totalArticles === 0;

  return (
    <WidgetContainer
      title="Article Stats"
      description="Your article performance"
      isLoading={isLoading}
      error={error}
      isEmpty={isEmpty}
      emptyMessage="Create your first article to see stats"
      size="medium"
    >
      <div className={styles.articleStats}>
        {/* Main stat */}
        <div className={styles.articleStats__main}>
          <div className={styles.articleStats__mainValue}>
            {data?.totalViews.toLocaleString() ?? 0}
          </div>
          <div className={styles.articleStats__mainLabel}>Total Views</div>
        </div>

        {/* Secondary stats */}
        {data && (
          <div className={styles.articleStats__secondary}>
            <div className={styles.articleStats__stat}>
              <span className={styles.articleStats__statValue}>
                {data.totalArticles}
              </span>
              <span className={styles.articleStats__statLabel}>Articles</span>
            </div>
            <div className={styles.articleStats__divider} />
            <div className={styles.articleStats__stat}>
              <span className={styles.articleStats__statValue}>
                {data.publicArticles}
              </span>
              <span className={styles.articleStats__statLabel}>Public</span>
            </div>
            <div className={styles.articleStats__divider} />
            <div className={styles.articleStats__stat}>
              <span className={styles.articleStats__statValue}>
                {data.totalArticles > 0
                  ? Math.round(data.totalViews / data.totalArticles)
                  : 0}
              </span>
              <span className={styles.articleStats__statLabel}>Avg Views</span>
            </div>
          </div>
        )}

        {/* Achievement badge */}
        {data && data.totalArticles >= 5 && (
          <div className={styles.articleStats__achievement}>
            {data.totalArticles >= 20 && '📚 Prolific Writer!'}
            {data.totalArticles >= 10 && data.totalArticles < 20 && '✍️ Active Contributor!'}
            {data.totalArticles >= 5 && data.totalArticles < 10 && '📝 Getting Started!'}
          </div>
        )}
      </div>
    </WidgetContainer>
  );
}
